import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Separator } from '@/components/ui/separator'
import { IssueReportForm } from './IssueReportForm'
import { ChevronDown, Settings, MessageSquare, Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'
import { totalMcpTools, totalSdkMethods } from '@/client/constants/marketing-data'

/**
 * SystemInfoSection Component
 *
 * A collapsible section at the bottom of the dashboard that contains:
 * - Feedback/issue report form (primary purpose)
 * - System/tech stack information (collapsed by default)
 *
 * This replaces the prominent tech stack display with something more user-focused.
 */
export function SystemInfoSection() {
  const [isOpen, setIsOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<'feedback' | 'about'>('feedback')

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card className="border-dashed">
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Settings className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-base">Help & System Info</CardTitle>
              </div>
              <ChevronDown
                className={cn(
                  'h-4 w-4 text-muted-foreground transition-transform',
                  isOpen && 'rotate-180'
                )}
              />
            </div>
            <CardDescription>
              Report issues, request features, or view system information
            </CardDescription>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="pt-0">
            {/* Tab buttons */}
            <div className="flex gap-2 mb-4">
              <Button
                variant={activeTab === 'feedback' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveTab('feedback')}
                className="flex items-center gap-2"
              >
                <MessageSquare className="h-4 w-4" />
                Send Feedback
              </Button>
              <Button
                variant={activeTab === 'about' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveTab('about')}
                className="flex items-center gap-2"
              >
                <Sparkles className="h-4 w-4" />
                About This System
              </Button>
            </div>

            <Separator className="mb-4" />

            {/* Feedback Form */}
            {activeTab === 'feedback' && (
              <div className="max-w-md">
                <p className="text-sm text-muted-foreground mb-4">
                  Found a bug? Have a suggestion? We'd love to hear from you.
                </p>
                <IssueReportForm />
              </div>
            )}

            {/* About / Tech Stack Info */}
            {activeTab === 'about' && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold">L2Stack</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  A production-ready full-stack CRM starter kit for Cloudflare Workers
                </p>

                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Tech Stack:</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span className="text-primary">⚡</span>
                      Vite + React 19 + TypeScript
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span className="text-primary">🎨</span>
                      Tailwind v4 + shadcn/ui
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span className="text-primary">🔥</span>
                      Hono on Cloudflare Workers
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span className="text-primary">🗄️</span>
                      D1 Database + Drizzle ORM
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span className="text-primary">🔐</span>
                      better-auth + Google OAuth
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span className="text-primary">📦</span>
                      R2 Storage for Avatars
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <h4 className="text-sm font-medium">AI Integration:</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-primary">🤖</span>
                      <div>
                        <strong>7 MCP Servers</strong>
                        <span className="text-muted-foreground"> ({totalMcpTools}+ tools)</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-primary">🎙️</span>
                      <div>
                        <strong>Voice Widget</strong>
                        <span className="text-muted-foreground"> (ElevenLabs)</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-primary">📦</span>
                      <div>
                        <strong>TypeScript SDK</strong>
                        <span className="text-muted-foreground"> ({totalSdkMethods} methods)</span>
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                <p className="text-xs text-muted-foreground">
                  Built with care by Jezweb.
                </p>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  )
}
